import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { statisticsService } from '../../services/statisticsService';

// Async thunk to fetch all statistics
export const fetchStatistics = createAsyncThunk(
  'statistics/fetchStatistics',
  async (dateRange, { rejectWithValue }) => {
    try {
      const { startDate, endDate } = dateRange;
      const response = await statisticsService.getAllStatistics(startDate, endDate);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const initialState = {
  // Inventory statistics
  inventory: {
    totalStockQuantity: 0,
    totalStockValue: 0,
    totalStockCostValue: 0,
    totalItems: 0,
    lowStockItems: 0
  },
  
  // Financial statistics
  receivables: 0,
  payables: 0,
  
  // Cash flow statistics
  cashFlow: {
    operating: 0,
    investing: 0,
    financing: 0,
    netCashFlow: 0
  },
  
  // Income statement statistics
  incomeStatement: {
    revenue: 0,
    cogs: 0,
    totalExpenses: 0,
    netIncome: 0
  },
  
  // Balance sheet statistics
  balanceSheet: {
    currentAssets: 0,
    nonCurrentAssets: 0,
    totalAssets: 0,
    currentLiabilities: 0,
    nonCurrentLiabilities: 0,
    totalLiabilities: 0,
    shareCapital: 0,
    retainedEarnings: 0,
    totalEquity: 0
  },
  
  // Loading and error states
  loading: false,
  error: null,
  lastUpdated: null
};

const statisticsSlice = createSlice({
  name: 'statistics',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetStatistics: (state) => {
      return { ...initialState };
    },
    /** Hydrate inventory overview from session cache (tab remount) without refetch. */
    applyCachedInventoryOverview: (state, action) => {
      const p = action.payload;
      if (!p) return;
      state.inventory.totalItems = Number(p.totalItems) || 0;
      state.inventory.totalStockValue = Number(p.totalStockValue) || 0;
      state.inventory.totalStockCostValue = Number(p.totalStockCostValue) || 0;
      state.inventory.lowStockItems = Number(p.lowStockItems) || 0;
      state.inventory.totalStockQuantity = Number(p.totalStockQuantity) || 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch statistics
      .addCase(fetchStatistics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStatistics.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.lastUpdated = new Date().toISOString();
        
        // Update all statistics from the response
        const data = action.payload;
        
        // Inventory statistics
        if (data.inventory) {
          state.inventory.totalStockQuantity = data.inventory.totalQuantity || 0;
          state.inventory.totalStockValue = data.inventory.totalValue || 0;
          state.inventory.totalStockCostValue = data.inventory.totalCostValue || 0;
          state.inventory.totalItems = data.inventory.totalItems || 0;
          state.inventory.lowStockItems = data.inventory.lowStockItems || 0;
        }
        
        // Financial statistics
        state.receivables = data.receivables || 0;
        state.payables = data.payables || 0;
        
        // Cash flow statistics
        if (data.cashFlow) {
          state.cashFlow.operating = data.cashFlow.totals?.operating || 0;
          state.cashFlow.investing = data.cashFlow.totals?.investing || 0;
          state.cashFlow.financing = data.cashFlow.totals?.financing || 0;
          state.cashFlow.netCashFlow = data.cashFlow.netCashFlow || 0;
        }
        
        // Income statement statistics
        if (data.incomeStatement) {
          state.incomeStatement.revenue = data.incomeStatement.summary?.revenue || 0;
          state.incomeStatement.cogs = data.incomeStatement.summary?.cogs || 0;
          state.incomeStatement.totalExpenses = data.incomeStatement.summary?.totalExpenses || 0;
          state.incomeStatement.netIncome = data.incomeStatement.summary?.netIncome || 0;
        }
        
        // Balance sheet statistics
        if (data.balanceSheet) {
          state.balanceSheet.currentAssets = data.balanceSheet.currentAssets?.total || 0;
          state.balanceSheet.nonCurrentAssets = data.balanceSheet.nonCurrentAssets?.total || 0;
          state.balanceSheet.totalAssets = data.balanceSheet.totals?.assets || 0;
          state.balanceSheet.currentLiabilities = data.balanceSheet.currentLiabilities?.total || 0;
          state.balanceSheet.nonCurrentLiabilities = data.balanceSheet.nonCurrentLiabilities?.total || 0;
          state.balanceSheet.totalLiabilities = data.balanceSheet.totals?.liabilities || 0;
          state.balanceSheet.shareCapital = data.balanceSheet.equity?.shareCapitalAndReserves?.amount || 0;
          state.balanceSheet.retainedEarnings = data.balanceSheet.equity?.retainedEarnings?.amount || 0;
          state.balanceSheet.totalEquity = data.balanceSheet.equity?.total || 0;
        }
      })
      .addCase(fetchStatistics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

export const { clearError, resetStatistics, applyCachedInventoryOverview } =
  statisticsSlice.actions;
export default statisticsSlice.reducer;
